import React from 'react'

import { parseRules } from 'lib/validation'

import SelectOption from './Option'

import Select from './index.presentational'
import { Props } from './types'

const SelectContainer: React.FC<Props> = ({
  children,
  createdByCorrectParent,
  defaultValue, /* eslint-disable-line @typescript-eslint/no-unused-vars */ // prevent this from being passed in htmlProps
  loaded,
  loading,
  options,
  placeholder,
  renderOptions,
  required: requiredProp,
  ...htmlProps
}): React.ReactElement => {
  if (!createdByCorrectParent) {
    throw new Error(
      'Do not directly use the Form/Input/Select component. Instead, use Form/Input and pass a `type` prop of `select`.',
    )
  }

  const processChild = (child: React.ReactNode): React.ReactNode => {
    if (!React.isValidElement(child)) {
      return child
    }

    if (child && child.type === SelectOption) {
      return React.cloneElement(child, {
        ...child.props,
        isChecked: child.props.value === htmlProps.value,
        onChange: htmlProps.onChange,
      })
    }

    return child
  }

  const newChildren = (renderOptions && options && renderOptions(options).map(child => processChild(child))) ||
    (options && options.map((option): React.ReactElement => (
      React.createElement(
        SelectOption,
        {
          key: option,
          value: option,
        },
        option,
      )
    )).map(child => processChild(child))) ||
    (children && React.Children.map(children, child => processChild(child))) ||
    []

  const required =
    requiredProp ||
    (htmlProps.rules &&
      parseRules(htmlProps.rules) &&
      parseRules(htmlProps.rules).required)

  if (required && !loading && (options || React.Children.toArray(children).length)) {
    newChildren.unshift(
      React.createElement(
        SelectOption,
        {
          key: 'automatic-placeholder',
          value: '',
          disabled: true,
          hidden: true,
        },
        placeholder,
      ),
    )
  }

  return React.createElement(
    Select,
    {
      ...htmlProps,
      options,
      showLoading: loading,
      showLoadingError: !loaded && !options && !React.Children.toArray(children).length,
      value: htmlProps.value,
      onChange: (event, ...options): void => {
        let { value } = event.target
        if (value === 'true' || value === 'false') {
          value = JSON.parse(value)
        }
        htmlProps.onChange && htmlProps.onChange({
          ...event,
          target: {
            ...event.target,
            value,
          },
        }, ...options)
      },
    },
    newChildren,
  )
}

export * from './types'

export default SelectContainer
